
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PortfolioItem {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  beforeImage: string;
  afterImage: string;
}

export default function About() {
  const { t } = useTranslation('common');
  const [selectedProject, setSelectedProject] = useState<PortfolioItem | null>(null);

  const portfolioItems: PortfolioItem[] = [
    {
      id: 1,
      title: 'Living Room Transformation',
      category: 'Interior Design',
      description: 'Complete living room makeover with modern furniture, ambient lighting, and elegant decor. Transformed a traditional space into a contemporary haven with neutral tones and natural materials.',
      image: 'https://public.readdy.ai/ai/img_res/98d809f524f3a276a6b4dadec542ec2b.jpg',
      beforeImage: 'https://readdy.ai/api/search-image?query=outdated%20traditional%20living%20room%20before%20renovation%20old%20furniture%20dark%20colors%20cluttered%20space%20needs%20improvement%20simple%20background&width=600&height=450&seq=portfolio-living-before-001&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=modern%20minimalist%20living%20room%20interior%20design%20with%20neutral%20beige%20tones%20natural%20wood%20furniture%20ambient%20lighting%20elegant%20contemporary%20decor%20clean%20lines%20comfortable%20seating%20area%20simple%20background%20professional%20photography&width=600&height=450&seq=portfolio-living-after-001&orientation=landscape',
    },
    {
      id: 2,
      title: 'Kitchen Renovation',
      category: 'Renovation',
      description: 'Full kitchen renovation featuring custom cabinetry, marble countertops, and state-of-the-art appliances. Created a functional and beautiful cooking space with optimal storage solutions.',
      image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20interior%20design%20with%20white%20marble%20countertops%20custom%20wooden%20cabinets%20stainless%20steel%20appliances%20pendant%20lighting%20clean%20organized%20space%20simple%20background%20professional%20photography&width=800&height=600&seq=portfolio-kitchen-001&orientation=landscape',
      beforeImage: 'https://readdy.ai/api/search-image?query=old%20outdated%20kitchen%20before%20renovation%20worn%20cabinets%20dated%20appliances%20needs%20improvement%20simple%20background&width=600&height=450&seq=portfolio-kitchen-before-001&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20interior%20design%20with%20white%20marble%20countertops%20custom%20wooden%20cabinets%20stainless%20steel%20appliances%20pendant%20lighting%20clean%20organized%20space%20simple%20background%20professional%20photography&width=600&height=450&seq=portfolio-kitchen-after-001&orientation=landscape',
    },
    {
      id: 3,
      title: 'Bedroom Makeover',
      category: 'Interior Design',
      description: 'Serene bedroom transformation with soft textures, calming colors, and thoughtful lighting. Designed to create a peaceful retreat for rest and relaxation.',
      image: 'https://readdy.ai/api/search-image?query=serene%20modern%20bedroom%20interior%20design%20with%20soft%20neutral%20colors%20comfortable%20bed%20elegant%20bedding%20ambient%20lighting%20peaceful%20atmosphere%20minimalist%20decor%20simple%20background%20professional%20photography&width=800&height=600&seq=portfolio-bedroom-001&orientation=landscape',
      beforeImage: 'https://readdy.ai/api/search-image?query=plain%20basic%20bedroom%20before%20makeover%20simple%20furniture%20needs%20improvement%20outdated%20decor%20simple%20background&width=600&height=450&seq=portfolio-bedroom-before-001&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=serene%20modern%20bedroom%20interior%20design%20with%20soft%20neutral%20colors%20comfortable%20bed%20elegant%20bedding%20ambient%20lighting%20peaceful%20atmosphere%20minimalist%20decor%20simple%20background%20professional%20photography&width=600&height=450&seq=portfolio-bedroom-after-001&orientation=landscape',
    },
    {
      id: 4,
      title: 'Bathroom Upgrade',
      category: 'Renovation',
      description: 'Luxurious bathroom renovation with spa-like features, premium fixtures, and elegant tilework. Transformed into a modern sanctuary for daily wellness rituals.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20modern%20bathroom%20interior%20design%20with%20elegant%20white%20tiles%20premium%20fixtures%20rainfall%20shower%20marble%20accents%20spa-like%20atmosphere%20clean%20minimalist%20space%20simple%20background%20professional%20photography&width=800&height=600&seq=portfolio-bathroom-001&orientation=landscape',
      beforeImage: 'https://readdy.ai/api/search-image?query=old%20dated%20bathroom%20before%20renovation%20worn%20fixtures%20outdated%20tiles%20needs%20improvement%20simple%20background&width=600&height=450&seq=portfolio-bathroom-before-001&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=luxury%20modern%20bathroom%20interior%20design%20with%20elegant%20white%20tiles%20premium%20fixtures%20rainfall%20shower%20marble%20accents%20spa-like%20atmosphere%20clean%20minimalist%20space%20simple%20background%20professional%20photography&width=600&height=450&seq=portfolio-bathroom-after-001&orientation=landscape',
    },
  ];

  return (
    <>
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl text-[#2B2B2B] mb-6">
              {t('about.title')}
            </h2>
            <div className="text-gray-600 text-lg max-w-4xl mx-auto leading-relaxed space-y-6 text-left">
              <p>
                {t('about.paragraph1')}
              </p>
              <p>
                {t('about.paragraph2')}
              </p>
              <p>
                {t('about.paragraph3')}
              </p>
            </div>
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolioItems.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedProject(item)}
                className="group relative h-96 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="w-full h-full">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <p className="text-white font-semibold text-lg">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedProject(null)}
              className="sticky top-4 left-full -ml-16 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-all cursor-pointer shadow-lg"
            >
              <i className="ri-close-line text-2xl text-gray-700"></i>
            </button>

            {/* Modal Content */}
            <div className="p-8 lg:p-12 -mt-16">
              {/* Header */}
              <div className="mb-8">
                <p className="text-[#8B9B7E] font-semibold mb-2">{selectedProject.category}</p>
                <h3 className="font-serif text-4xl text-[#2B2B2B] mb-4">
                  {selectedProject.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>

              {/* Before & After Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">BEFORE</p>
                  <div className="w-full h-80 rounded-xl overflow-hidden">
                    <img
                      src={selectedProject.beforeImage}
                      alt="Before"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">AFTER</p>
                  <div className="w-full h-80 rounded-xl overflow-hidden">
                    <img
                      src={selectedProject.afterImage}
                      alt="After"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    setTimeout(() => {
                      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                  }}
                  className="bg-[#8B9B7E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all whitespace-nowrap cursor-pointer"
                >
                  Book Similar Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
